import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  endWebConversation,
  fetchWaitingState,
  requestWhatsappContinuation,
  submitHandoverForm,
  submitFeedback,
  submitInlineFollowupAction,
} from '../lib/api';
import { extractSseEvents } from '../lib/sse';
import { parseMarkdown, buildAnswerUrl, pushUrl, replaceUrl } from '../lib/utils';
import { buildAnalyticsPayload, getAnonymousUserId } from '../lib/user-context';
import {
  clearChatSession,
  launchAlreadyHandled,
  loadChatSession,
  saveChatSession,
} from '../lib/chatSession';
import { postUnread, useHostPanelOpen, useWebsiteVisitId } from '../lib/hostBridge';
import { useClient } from '../context/ClientContext';
import { useDevMode } from '../context/DevModeContext';
import SourceCard from './sources/SourceCard';
import CitationList from './sources/CitationList';
import SkillDisplay from './SkillDisplay';
import FeedbackButtons from './FeedbackButtons';
import { useAutocomplete, AutocompleteSuggestions } from '../features/autocomplete';
import InlineFollowupQuestion from './InlineFollowupQuestion';
import HandoverFormCard from './HandoverFormCard';
import {
  MOCK_AUTH_SUCCESS_CODE,
  isCuratedAnswer,
  getStageLabel,
  getPendingStatusMessage,
  speaksPortuguese,
  getStageClasses,
  getToolLabel,
  getToolClasses,
  getLiveWebMeta,
  getStructuredDisplay,
  readSessionCookie,
  normalizeMockAuthCodeInput,
  handoverDividers,
  authoredMessages,
  createIdleWatchdog,
  recentConversationFor,
  STREAM_IDLE_TIMEOUT_MS,
} from './askbox-utils';
import { useMockAuth } from '../hooks/useMockAuth';
import { useCopy } from '../lib/copy';
import { conversationReferral, referralFromSearch } from '../lib/referral';
import { offeredHandoverForm } from '../lib/handoverContact';

function StreamStatusPanel({
  currentStage,
  statusMessage,
  currentTool,
  currentToolQuery,
  currentToolTargets,
  showStageLabel,
}) {
  const hasPrimaryStatus = Boolean(statusMessage || currentStage);
  const hasToolStatus = currentTool === 'search_web';

  if (!hasPrimaryStatus && !hasToolStatus) {
    return null;
  }

  return (
    <div className="space-y-2">
      {hasPrimaryStatus && (
        <div className="space-y-1">
          {currentStage && showStageLabel && (
            <span
              className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] ${getStageClasses(currentStage)}`}
            >
              {getStageLabel(currentStage)}
            </span>
          )}
          {statusMessage && <div className="text-sm text-slate-500">{statusMessage}</div>}
        </div>
      )}

      {hasToolStatus && (
        <div className="rounded-2xl border border-slate-200 bg-white/80 px-3 py-2 shadow-sm shadow-slate-200/40">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] ${getToolClasses(currentTool)}`}
            >
              {getToolLabel(currentTool)}
            </span>
            {currentToolTargets && (
              <span className="text-xs text-slate-500">Checking {currentToolTargets}</span>
            )}
          </div>
          {currentToolQuery && (
            <div className="mt-1 text-xs text-slate-500">
              Search: <span className="font-medium text-slate-600">{currentToolQuery}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * AskBox - Main chat interface for asking questions
 * Split into ChatInput (fixed at bottom) and ChatMessages (scrollable)
 * Enhanced with markdown support and progressive source rendering
 */
export default function AskBox({
  clientId,
  personaId,
  initialQuery = '',
  initialAnswerId = null,
  scrollContainerRef = null,
  preferredScenarioCode: initialPreferredScenarioCode = null,
  surfaceMode = 'page',
}) {
  const { clientConfig } = useClient();
  const t = useCopy();
  const websiteVisitId = useWebsiteVisitId();
  const hasSeededLaunch = Boolean(
    String(initialQuery || '').trim() || initialAnswerId || initialPreferredScenarioCode,
  );
  // The chat this browser kept from before a refresh or a closed tab
  // (lib/chatSession.js), read once.
  const [restored] = useState(() => loadChatSession(clientId));
  const [launchReferral] = useState(() => referralFromSearch(window.location.search));
  const [referralCode, setReferralCode] = useState(() =>
    conversationReferral(window.location.search, restored?.referralCode),
  );
  // A refresh keeps the URL: a launch the restored chat already answered is
  // not sent again.
  const [launchHandled] = useState(() =>
    restored
      ? launchAlreadyHandled(restored.conversation, { initialQuery, initialAnswerId })
      : false,
  );
  const [query, setQuery] = useState(launchHandled ? '' : initialQuery);
  const [isLoading, setIsLoading] = useState(false);
  const [conversation, setConversation] = useState(() => restored?.conversation || []);
  // True while a person, rather than the assistant, is answering this visitor.
  // Their replies arrive on WhatsApp long after the request that asked for
  // help returned, so the page has to go and look for them.
  const [handledByHuman, setHandledByHuman] = useState(() => restored?.handledByHuman || false);
  // The capability that lets this page read its own conversation with a
  // person. Minted server-side when the handover opened and sent once, in that
  // turn's response; only its SHA-256 is stored server-side. Kept with the rest
  // of the chat (Alex, 2026-10-12): a refresh that dropped it left the visitor
  // unable to receive the reply they were waiting for. In localStorage for 24
  // hours after the last message (Alex, 2026-10-13), so a closed tab keeps it too.
  const [conversationReadToken, setConversationReadToken] = useState(
    () => restored?.conversationReadToken || null,
  );
  // True once the assistant told this visitor the team would be in touch and
  // the team was told. The assistant keeps answering, so `handledByHuman` stays
  // false, but a person's reply can arrive at any moment and must be looked for.
  const [awaitingPerson, setAwaitingPerson] = useState(() => restored?.awaitingPerson || false);
  // Phase 2: the server says whether "Continuar no WhatsApp" may be offered, and
  // whether this conversation already moved there, which ends it on the web.
  const [whatsappAvailable, setWhatsappAvailable] = useState(false);
  const [continuedOnWhatsapp, setContinuedOnWhatsapp] = useState(
    () => restored?.continuedOnWhatsapp || false,
  );
  const [offeringWhatsapp, setOfferingWhatsapp] = useState(false);
  const [whatsappError, setWhatsappError] = useState(null);
  const [sessionId, setSessionId] = useState(() => restored?.sessionId || null);
  const [userInfo, setUserInfo] = useState(null); // User info from Gateway API
  const [statusMessage, setStatusMessage] = useState('');
  const [currentStage, setCurrentStage] = useState(null); // Track query stage (memory, rag, etc.)
  const [currentTool, setCurrentTool] = useState(null);
  const [currentToolQuery, setCurrentToolQuery] = useState('');
  const [currentToolTargets, setCurrentToolTargets] = useState('');
  const [questionSource, setQuestionSource] = useState('typed');
  const [inlineFollowupQuestion, setInlineFollowupQuestion] = useState(null); // Current inline follow-up question to show
  const [inlineFollowupMessageIndex, setInlineFollowupMessageIndex] = useState(null); // Which message it follows
  const [isInlineFollowupSubmitting, setIsInlineFollowupSubmitting] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const initialQueryLoadedRef = useRef(launchHandled);
  const directLinkLoadedRef = useRef(launchHandled);

  // Autocomplete hook - curated answer suggestions
  // When enabled: uses browser-based embeddings (with server API fallback)
  // Requires: python3 -m http.server 8080 (from concierge-app/public folder) for local embeddings
  const autocomplete = useAutocomplete(clientId, {
    enabled: clientConfig?.autocomplete || false,
    limit: 5,
    useLocalEmbeddings: clientConfig?.autocomplete || false, // Use local when autocomplete is enabled
  });

  const isWindowSurface = surfaceMode !== 'page';
  const isCompactWindowSurface = surfaceMode === 'compact' || surfaceMode === 'mobile';

  // Conversation-side callbacks that the auth hook needs
  const onConversationClearAuthPrompt = useCallback(() => {
    setConversation((prev) => {
      const updated = [...prev];
      for (let i = updated.length - 1; i >= 0; i -= 1) {
        if (updated[i]?.role === 'assistant' && updated[i]?.authPrompt) {
          updated[i] = { ...updated[i], authPrompt: null, authGated: true };
          break;
        }
      }
      return updated;
    });
  }, []);

  // A message the page did not generate: either a person from the team, or a
  // reviewed service notice. `actor` is what lets the bubble say which, and
  // joining them into one string is how a manager's reply came to be labelled
  // with the assistant's name.
  const humanOrServiceMessage = (message) => ({
    role: 'assistant',
    content: message.body,
    citations: [],
    confidence: null,
    reasoning: '',
    request_id: null,
    // A person's words are not a generated answer: no feedback buttons, no
    // confidence badge, and not the assistant's name underneath.
    fromHuman: message.actor === 'human',
    // The channel speaking (for example "Vou chamar alguém da equipe"): not a
    // person, and not the assistant coming back either.
    fromService: message.actor === 'service',
  });

  // Staff replies that arrived while the bubble's panel was closed, so the site
  // can show that one came (lib/hostBridge.js). Zero on a page of its own.
  const panelOpen = useHostPanelOpen();
  const panelOpenRef = useRef(panelOpen);
  const [unreadReplies, setUnreadReplies] = useState(0);
  useEffect(() => {
    panelOpenRef.current = panelOpen;
    if (panelOpen) {
      setUnreadReplies(0);
    }
  }, [panelOpen]);
  const waitingForPerson =
    (handledByHuman || awaitingPerson) &&
    !continuedOnWhatsapp &&
    Boolean(sessionId && conversationReadToken);
  useEffect(() => {
    postUnread({ count: unreadReplies, active: waitingForPerson });
  }, [unreadReplies, waitingForPerson]);

  // While a person is answering, go and look for what they have said.
  //
  // Their reply arrives on WhatsApp minutes after the request that asked for
  // help already returned, so there is no event to wait for — a poll is the
  // only way a browser hears one. It runs only while a case is open, stops the
  // moment the server stops saying so, and a failed poll is simply the next
  // poll: a visitor waiting for a person must never be shown an error because
  // a background request missed.
  useEffect(() => {
    // No token means no capability, and polling without one is a request the
    // server is right to refuse. Nothing to show and nothing to retry.
    if (
      !(handledByHuman || awaitingPerson) ||
      continuedOnWhatsapp ||
      !sessionId ||
      !clientId ||
      !conversationReadToken
    ) {
      return undefined;
    }
    let cancelled = false;

    const check = async () => {
      const state = await fetchWaitingState(
        clientId,
        sessionId,
        getAnonymousUserId(),
        conversationReadToken,
      );
      if (cancelled) {
        return;
      }
      setWhatsappAvailable(state.whatsappContinuationAvailable);
      if (state.continuedOnWhatsapp) {
        setContinuedOnWhatsapp(true);
      }
      if (state.messages.length > 0) {
        setConversation((prev) => [...prev, ...state.messages.map(humanOrServiceMessage)]);
        const fromPeople = state.messages.filter((message) => message.actor === 'human').length;
        if (fromPeople > 0 && !panelOpenRef.current) {
          setUnreadReplies((count) => count + fromPeople);
        }
      }
      // The case closed (staff, or the nightly close): say so once, after any
      // last reply above, and stop waiting. Clearing both flags ends this poll,
      // and the assistant answers whatever the visitor writes next (D7 of
      // docs/plans/2026-10-15-npas-open-issues).
      if (state.caseClosed) {
        setConversation((prev) => [
          ...prev,
          humanOrServiceMessage({
            body: t('This request was closed. If you need anything else, just write here.'),
            actor: 'service',
          }),
        ]);
        setHandledByHuman(false);
        setAwaitingPerson(false);
      }
    };

    const timer = setInterval(check, 5000);
    check();
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [
    handledByHuman,
    awaitingPerson,
    continuedOnWhatsapp,
    sessionId,
    clientId,
    conversationReadToken,
  ]);

  // Keep the chat, so a refresh or a closed tab does not lose it (lib/chatSession.js).
  useEffect(() => {
    saveChatSession(clientId, {
      sessionId,
      conversation,
      handledByHuman,
      awaitingPerson,
      conversationReadToken,
      continuedOnWhatsapp,
      referralCode,
    });
  }, [
    clientId,
    sessionId,
    conversation,
    handledByHuman,
    awaitingPerson,
    conversationReadToken,
    continuedOnWhatsapp,
    referralCode,
  ]);

  // The visitor's own "Continuar no WhatsApp" (Phase 2): the same invitation a
  // manager sends, shown at once.
  const offerWhatsapp = async () => {
    setOfferingWhatsapp(true);
    setWhatsappError(null);
    const result = await requestWhatsappContinuation(
      clientId,
      sessionId,
      getAnonymousUserId(),
      conversationReadToken,
    );
    setOfferingWhatsapp(false);
    if (result.error) {
      setWhatsappError(t('Could not open WhatsApp now. Please try again.'));
      return;
    }
    setConversation((prev) => [...prev, ...result.messages.map(humanOrServiceMessage)]);
  };

  // The web handover form, sent (docs/plans/2026-10-11-web-handover-form,
  // contract 3). The case opens on the server, and what comes back is what a
  // handover turn returns: the reviewed notice, `handled_by`, and the token the
  // form was offered with, so the poll for a person's reply starts as before.
  // `sent` is what the card shows once the form went through. It is kept on
  // the message, so a refresh shows the form as sent rather than offering it
  // again.
  const sendHandoverForm = async (form, fields, sent = null) => {
    const result = await submitHandoverForm(
      clientId,
      sessionId,
      getAnonymousUserId(),
      form.conversation_read_token,
      fields,
    );
    const shown = result.error ? result.messages : result.data.messages;
    if (shown.length > 0) {
      setConversation((prev) => [...prev, ...shown.map(humanOrServiceMessage)]);
    }
    if (result.error) {
      return { error: result.error };
    }
    if (!result.data.conversation_read_token) {
      console.error(
        'API contract violation: no conversation_read_token after the form',
        result.data,
      );
      return { error: 'request_failed' };
    }
    let shownSent = sent;
    if (sent?.kind === 'whatsapp' && result.data.status === 'opened') {
      // The number as the API saved it, with its country code (Task 10).
      if (!result.data.whatsapp) {
        console.error('API contract violation: no saved whatsapp after the form', result.data);
        return { error: 'request_failed' };
      }
      shownSent = { ...sent, value: result.data.whatsapp };
    }
    setConversationReadToken(result.data.conversation_read_token);
    setHandledByHuman(result.data.handled_by === 'human');
    if (shownSent) {
      setConversation((prev) =>
        prev.map((message) =>
          message.handoverForm === form ? { ...message, handoverFormSent: shownSent } : message,
        ),
      );
    }
    return { ok: true, sent: shownSent };
  };

  // Ref-based callback so the auth hook can re-submit after verification
  // without a circular dependency on handleSubmit.
  const submitAfterAuthRef = useRef(null);
  // Cancels an in-flight next-action typing animation if the user starts typing.
  const cancelNextActionAnimationRef = useRef(false);
  const onSubmitAfterAuth = useCallback(async (opts) => {
    if (submitAfterAuthRef.current) {
      await submitAfterAuthRef.current(opts);
    }
  }, []);

  const onClearConversation = useCallback(() => {
    clearChatSession(clientId);
    setConversation([]);
    setSessionId(null);
    setHandledByHuman(false);
    setAwaitingPerson(false);
    setConversationReadToken(null);
    setUserInfo(null);
    setStatusMessage('');
    setCurrentStage(null);
    setCurrentTool(null);
    setCurrentToolQuery('');
    setCurrentToolTargets('');
    setInlineFollowupQuestion(null);
    setInlineFollowupMessageIndex(null);
    setWhatsappAvailable(false);
    setContinuedOnWhatsapp(false);
    setWhatsappError(null);
    // Reset URL to client root (remove answer path)
    replaceUrl(`/${clientId}`);
  }, [clientId]);

  const auth = useMockAuth(clientId, clientConfig, {
    initialPreferredScenarioCode,
    hasSeededLaunch,
    initialQuery,
    initialAnswerId,
    conversationStarted: conversation.length > 0,
    onConversationClearAuthPrompt,
    onClearConversation,
    onSubmitAfterAuth,
  });

  // Auto-focus the input on mount (desktop only)
  // On mobile, we skip auto-focus to avoid immediately showing keyboard
  useEffect(() => {
    // Check if likely desktop (no touch or wide screen)
    const isDesktop = !('ontouchstart' in window) || window.innerWidth >= 1024;

    if (isDesktop) {
      // Small delay to ensure DOM is ready (especially after navigation)
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, []);

  // 🍪 DEBUG: Check cookies on mount
  useEffect(() => {
    console.log('\n' + '='.repeat(60));
    console.log('🍪 [COOKIE CHECK] Page loaded - checking for session cookie');
    console.log('='.repeat(60));
    console.log('📍 Location:', window.location.href);
    console.log('🌐 Hostname:', window.location.hostname);
    console.log('🔒 Protocol:', window.location.protocol);
    console.log('🔢 Port:', window.location.port || '(default)');

    const allCookies = document.cookie.split(';').reduce((acc, cookie) => {
      const [name, value] = cookie.trim().split('=');
      acc[name] = value;
      return acc;
    }, {});

    console.log('🍪 Total cookies found:', Object.keys(allCookies).length);
    console.log('🍪 Cookie names:', Object.keys(allCookies));

    if (allCookies.session) {
      try {
        const sessionCookieValue = decodeURIComponent(allCookies.session);
        const sessionCookieJson = JSON.parse(sessionCookieValue);
        console.log('✅ Session cookie found!');
        console.log('   Session ID:', sessionCookieJson.session_id);
        console.log('   Full cookie:', sessionCookieJson);
      } catch (e) {
        console.log('⚠️  Session cookie found but cannot parse as JSON');
        console.log('   Raw value:', allCookies.session);
      }
    } else {
      console.log('❌ No "session" cookie found');
      console.log('💡 Possible reasons:');
      console.log('   1. Not logged in to profiles.app.thoughtfabric.com');
      console.log('   2. Cookie domain mismatch (check if cookie is for .app.thoughtfabric.com)');
      console.log('   3. Port mismatch (cookie set on :443 but accessing on :3000)');
      console.log('   4. HttpOnly flag (check DevTools > Application > Cookies)');
      console.log('   5. Secure flag and not using HTTPS');
    }
    console.log('='.repeat(60) + '\n');
  }, []); // Run once on mount

  // Listen for spot events - auto-submit immediately
  useEffect(() => {
    const handleSpot = (event) => {
      const { query: spotQuery, source, answer_id } = event.detail;
      // Pass source directly to handleSubmit to avoid React state timing issues
      // (setState is async, so questionSource wouldn't be updated yet)
      // Pass answer_id if available (for curated answers - skips "Searching..." animation)
      handleSubmit(null, spotQuery, source || 'spot', answer_id || null);
    };
    window.addEventListener('spot', handleSpot);
    return () => window.removeEventListener('spot', handleSpot);
  }, []);

  // Auto-submit query if provided via URL parameter (?q=)
  useEffect(() => {
    if (
      !initialQuery ||
      !initialQuery.trim() ||
      initialQueryLoadedRef.current ||
      auth.shouldAutoSwitchScenario
    ) {
      return undefined;
    }
    setQuestionSource('url_param');
    const timer = setTimeout(() => {
      initialQueryLoadedRef.current = true;
      handleSubmit();
    }, 100);
    return () => clearTimeout(timer);
  }, [initialQuery, auth.shouldAutoSwitchScenario]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-load answer if provided via URL path (/client/answer/{answerId})
  useEffect(() => {
    // Guard against React strict mode double-invoke
    if (initialAnswerId && !directLinkLoadedRef.current && !auth.shouldAutoSwitchScenario) {
      directLinkLoadedRef.current = true;
      setQuestionSource('direct_link');
      // Trigger submit with the answer_id to fetch directly
      const timer = setTimeout(() => {
        handleSubmit(null, '(Direct link)', 'direct_link', initialAnswerId);
      }, 100);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [initialAnswerId, auth.shouldAutoSwitchScenario]); // eslint-disable-line react-hooks/exhaustive-deps

  const clearActiveClarification = () => {
    setConversation((prev) => {
      const updated = [...prev];
      for (let i = updated.length - 1; i >= 0; i -= 1) {
        if (updated[i]?.role === 'assistant' && updated[i]?.clarificationQuestion) {
          updated[i] = { ...updated[i], clarificationQuestion: null };
          break;
        }
      }
      return updated;
    });
    setInlineFollowupQuestion(null);
    setInlineFollowupMessageIndex(null);
  };

  const buildLoopClarificationFollowUp = (selectedAnswer, questionConfig) => {
    const scopedChoice = (selectedAnswer || '').trim();
    const latestUserQuestion =
      [...conversation]
        .reverse()
        .find((message) => message.role === 'user')
        ?.content?.trim() || '';
    const selectedLower = scopedChoice.toLowerCase();

    const promptParts = [
      latestUserQuestion ? `Original question: "${latestUserQuestion}"` : null,
      `Chosen angle: "${scopedChoice}"`,
      selectedLower.includes('abo-ncle')
        ? 'Continue with the ABO-NCLE certification side only.'
        : null,
      selectedLower.includes('uoa') ? 'Continue with the UOA membership side only.' : null,
      selectedLower.includes('side-by-side')
        ? 'Continue with the direct side-by-side comparison.'
        : null,
      selectedLower.includes('renewal')
        ? 'Continue with renewal-related costs and requirements only.'
        : null,
      'Continue the answer from that angle.',
    ];

    return {
      displayMessage: scopedChoice,
      requestQuestion: promptParts.filter(Boolean).join('\n'),
    };
  };

  const handleSubmit = async (
    e,
    overrideQuery = null,
    overrideSource = null,
    answerId = null,
    displayOverride = null,
    options = {},
  ) => {
    e?.preventDefault();
    const messageToSend = overrideQuery || query;
    // The conversation moved to WhatsApp; nothing more is taken here (spec §11).
    if (!messageToSend.trim() || isLoading || continuedOnWhatsapp) return;

    const userMessage = messageToSend.trim();
    const displayMessage = (displayOverride || userMessage).trim();
    // Use overrideSource if provided (for quick actions), otherwise use state
    const effectiveSource = overrideSource || questionSource;
    // answer_id for direct curated answer fetch (bypasses semantic search)
    const effectiveAnswerId = answerId;
    setQuery('');
    setIsLoading(true);
    setCurrentTool(null);
    setCurrentToolQuery('');
    setCurrentToolTargets('');

    // Skip elaborate "Searching..." animation for direct curated answer fetches
    // These are instant database lookups, not RAG searches
    const isCuratedFetch = !!effectiveAnswerId;
    console.log('🔍 [LOADING] isCuratedFetch:', isCuratedFetch, 'answerId:', effectiveAnswerId);

    setStatusMessage(getPendingStatusMessage({ isCuratedFetch, locale: clientConfig?.locale }));
    setCurrentStage(isCuratedFetch ? 'direct' : 'review');

    // Add user message to conversation (skip for direct links - we'll get the query from API)
    if (effectiveSource !== 'direct_link' && !options.skipUserEcho) {
      setConversation((prev) => [...prev, { role: 'user', content: displayMessage }]);
    }

    // Use existing session ID or null (API will generate on first request)
    const currentSessionId = sessionId; // null on first request, API is authoritative
    if (!sessionId) {
      console.log('First request - API will generate session_id');
    } else {
      console.log('Using existing session_id:', currentSessionId);
    }

    const { cookies: allCookies, session: parsedCookieSession } = readSessionCookie();
    const authSessionId = auth.resolveAuthSessionId(options.forcedAuthSessionId);

    if (parsedCookieSession?.session_id) {
      console.log('🍪 [COOKIES] Session cookie (parsed JSON):', parsedCookieSession);
      console.log('🔑 [AUTH] Extracted auth_session_id:', parsedCookieSession.session_id);
    } else if (!auth.mockAuthSession?.authSessionId) {
      console.warn('🍪 [COOKIES] ❌ No "session" cookie found!');
      console.log('🍪 [COOKIES] This may be because:');
      console.log('  1. Cookie has Secure flag and site is using HTTP (expected HTTPS)');
      console.log('  2. Cookie domain mismatch');
      console.log('  3. Cookie not set yet');
      console.log(
        '  4. HttpOnly flag prevents JavaScript access (check browser DevTools > Application > Cookies)',
      );
    } else {
      console.log('🔑 [AUTH] Using stored mock auth session:', auth.mockAuthSession.authSessionId);
    }

    // 📊 Build analytics payload and preserve the stable anonymous ID even when verified
    const analytics = buildAnalyticsPayload(authSessionId);
    const anonymousId = getAnonymousUserId();
    const recentConversation = recentConversationFor(conversation);

    // 🚀 Build complete request payload
    const requestPayload = {
      question: userMessage,
      client_id: clientId,
      session_id: currentSessionId, // Chat conversation session
      auth_session_id: authSessionId, // Authentication session from SSO cookie or staged mock auth
      anonymous_id: anonymousId, // Stable anonymous ID (kept even after login)
      client_info: {
        ...analytics.client_info,
        ...(referralCode === null ? {} : { referral_code: referralCode }),
        ...(websiteVisitId === null ? {} : { website_visit_id: websiteVisitId }),
      },
      question_source: effectiveSource,
      answer_id: effectiveAnswerId, // Direct curated answer fetch (bypasses semantic search)
      recent_conversation: recentConversation,
    };

    console.log('🚀 [FRONTEND] Sending request:', requestPayload);
    console.log('👤 [ANALYTICS] Anonymous user:', anonymousId);
    console.log('📋 Session Context:', {
      client_id: clientId,
      chat_session_id: currentSessionId, // Chat session (for conversation history)
      auth_session_id: authSessionId, // Auth session (for user identity)
      is_new_session: !sessionId,
      message_count: conversation.length,
    });
    console.log('🍪 [COOKIES] Current location:', {
      hostname: window.location.hostname,
      protocol: window.location.protocol,
      href: window.location.href,
    });
    console.log('🍪 [COOKIES] All cookies:', allCookies);
    console.log('🍪 [COOKIES] Total cookies found:', Object.keys(allCookies).length);
    console.log('🍪 [COOKIES] Cookie names:', Object.keys(allCookies));

    let sawCompleteEvent = false;
    let timedOut = false;
    const streamAbort = new AbortController();
    const watchdog = createIdleWatchdog(STREAM_IDLE_TIMEOUT_MS, () => {
      timedOut = true;
      console.error('Chat stream idle timeout', { clientId, sessionId: currentSessionId });
      streamAbort.abort();
    });
    watchdog.kick();

    try {
      // Use streaming API for real-time updates and augmentation support.
      // Empty default → relative URL leverages Vite's dev proxy.
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
      const response = await fetch(`${apiBaseUrl}/api/ask/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestPayload),
        signal: streamAbort.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Read streaming response
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let streamedAssistantKey = null;
      let sawDoneEvent = false;

      const processEvent = (event) => {
        if (event.type === '__done__') {
          sawDoneEvent = true;
          return;
        }

        switch (event.type) {
          case 'status':
            setStatusMessage(event.message);
            // Track stage for conditional UI (e.g., hide spotlight for memory queries)
            if (event.stage) {
              setCurrentStage(event.stage);
            }
            if (event.tool) {
              setCurrentTool(event.tool);
              setCurrentToolQuery(typeof event.query === 'string' ? event.query : '');
              setCurrentToolTargets(typeof event.targets === 'string' ? event.targets : '');
            }
            break;

          case 'text_delta':
            if (!event.delta) {
              break;
            }
            if (streamedAssistantKey === null) {
              streamedAssistantKey = `stream-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
            }
            const currentStreamKey = streamedAssistantKey;
            setConversation((prev) => {
              const newMessages = [...prev];
              const existingIndex = newMessages.findIndex(
                (msg) => msg?._streamKey === currentStreamKey,
              );
              if (existingIndex >= 0) {
                newMessages[existingIndex] = {
                  ...newMessages[existingIndex],
                  content: `${newMessages[existingIndex].content || ''}${event.delta}`,
                };
                return newMessages;
              }
              newMessages.push({
                role: 'assistant',
                content: event.delta,
                citations: [],
                confidence: null,
                reasoning: '',
                request_id: null,
                isStreaming: true,
                _streamKey: currentStreamKey,
              });
              return newMessages;
            });
            break;

          case 'user_info':
            // Capture user info if returned (only on first message)
            if (event.data && !userInfo) {
              setUserInfo(event.data);
              console.log('👤 [USER INFO] Captured from API:', event.data);
            }
            break;

          case 'complete':
            sawCompleteEvent = true;
            // A person is handling this now, or has stopped. Either way the
            // server says so on every turn, so the page never has to guess.
            setHandledByHuman(event.handled_by === 'human');
            if (event.awaiting_person) {
              setAwaitingPerson(true);
            }
            // Present only on the turn that opened the handover. Never clear
            // it on a later turn: the same conversation keeps the same
            // capability until another handover rotates it.
            if (event.conversation_read_token) {
              setConversationReadToken(event.conversation_read_token);
            }
            setCurrentStage(null); // Reset stage
            setCurrentTool(null);
            setCurrentToolQuery('');
            setCurrentToolTargets('');
            setStatusMessage('');

            // Capture session_id from complete event. Before anything that can
            // leave this branch early: polling for a person's reply is keyed on
            // the session, and a handover turn that returned before reaching
            // here left the page with nothing to poll with — so the reply sat
            // in the database, correct and unread, exactly as though the
            // feature did not work.
            if (event.session_id) {
              if (!sessionId) {
                setSessionId(event.session_id);
              }
              // One stable line per turn so a tester can tie a transcript to
              // the server's traces: grep the console for "[concierge] session_id=".
              console.info(`[concierge] session_id=${event.session_id}`);
            }

            // A promised handover keeps the reply that made the promise, so it
            // goes through the ordinary answer path below and its notice follows.
            // Rendered here rather than on that path, because these are stored
            // messages with an author and that path would attribute them to the
            // assistant.
            const authored = authoredMessages(event);
            if (authored) {
              if (authored.length > 0) {
                setConversation((prev) => [...prev, ...authored.map(humanOrServiceMessage)]);
              }
              setIsLoading(false);
              break;
            }

            // Update browser URL for shareable links (curated answers only)
            if (event.answer_id) {
              const answerUrl = buildAnswerUrl(clientId, event.answer_id);
              pushUrl(answerUrl);
              console.log('🔗 [URL] Updated to curated answer:', answerUrl);
            }

            // Add answer to conversation (with augmentation support)
            // For direct links, add the canonical query as user message first.
            // Compute the assistant index deterministically instead of relying on
            // React state timing, otherwise the follow-up can be anchored to -1.
            const hadUserMessageAdded = effectiveSource !== 'direct_link';
            const insertsCanonicalQuery =
              !hadUserMessageAdded && conversation.length === 0 && event.canonical_query;
            const assistantMessageIndex =
              conversation.length + (hadUserMessageAdded ? 1 : 0) + (insertsCanonicalQuery ? 1 : 0);
            const resolvedClarificationQuestion = event.clarification_question || null;
            const resolvedAuthPrompt = event.auth_prompt || null;
            const currentStreamKeyForComplete = streamedAssistantKey;
            let finalizedAssistantMessageIndex = assistantMessageIndex;

            setConversation((prev) => {
              const newMessages = [...prev];
              // If no user message yet (direct link), add the canonical query
              if (newMessages.length === 0 && event.canonical_query) {
                newMessages.push({ role: 'user', content: event.canonical_query });
              }
              const finalizedAssistantMessage = {
                role: 'assistant',
                content: event.answer,
                citations: event.citations,
                confidence: event.confidence,
                reasoning: event.reasoning,
                isCurated: Boolean(event.is_curated),
                isSystemError: Boolean(event.is_system_error),
                offeredLiveSearch: Boolean(event.offered_live_search),
                request_id: event.request_id,
                incident_id: event.incident_id || null,
                retryQuestion: userMessage,
                retryDisplayMessage: displayMessage,
                isAugmenting: event.rag_augmentation_enabled || false,
                augmentedContent: null,
                augmentedCitations: [],
                answer_id: event.answer_id, // Store for URL handling
                clarificationQuestion: resolvedClarificationQuestion,
                authPrompt: resolvedAuthPrompt,
                authGated: Boolean(resolvedAuthPrompt),
                usedTools: Array.isArray(event.used_tools) ? event.used_tools : [],
                webResearchUsed: Boolean(event.web_research_used),
                webSearchQuery:
                  typeof event.web_search_query === 'string' ? event.web_search_query : '',
                webSearchTargets:
                  typeof event.web_search_targets === 'string' ? event.web_search_targets : '',
                display: event.display && typeof event.display === 'object' ? event.display : null,
                nextActions: Array.isArray(event.next_actions) ? event.next_actions : null,
                // The web handover form: no case is open until it is sent.
                handoverForm: offeredHandoverForm(event, newMessages),
                handoverFormWhatsapp: event.whatsapp_continuation_available === true,
                isStreaming: false,
              };
              const existingStreamIndex = currentStreamKeyForComplete
                ? newMessages.findIndex((msg) => msg?._streamKey === currentStreamKeyForComplete)
                : -1;
              if (existingStreamIndex >= 0) {
                finalizedAssistantMessageIndex = existingStreamIndex;
                newMessages[existingStreamIndex] = finalizedAssistantMessage;
              } else {
                finalizedAssistantMessageIndex = newMessages.length;
                newMessages.push(finalizedAssistantMessage);
              }
              return newMessages;
            });
            setIsLoading(false);
            streamedAssistantKey = null;

            // The reply told them the team would be in touch: the reviewed notice
            // saying whether the team was actually reached follows it.
            if (event.handover_after_answer && Array.isArray(event.messages)) {
              setConversation((prev) => [...prev, ...event.messages.map(humanOrServiceMessage)]);
            }

            if (resolvedAuthPrompt) {
              auth.setFooterAuthOpen(false);
              auth.setAuthPrompt(resolvedAuthPrompt);
              auth.setAuthMessageIndex(finalizedAssistantMessageIndex);
              auth.setAuthFormOpen(false);
              auth.setAuthStep('code');
              auth.setAuthError('');
              auth.setPendingAuthResume({
                question: userMessage,
                displayMessage,
                source: effectiveSource,
              });
              console.log('🔐 [AUTH] Prompt ready:', resolvedAuthPrompt.title || 'Verify');
              break;
            }

            auth.setAuthPrompt(null);
            auth.setAuthMessageIndex(null);
            auth.setAuthFormOpen(false);

            const loopClarificationQuestion = resolvedClarificationQuestion;
            if (loopClarificationQuestion) {
              setInlineFollowupQuestion(loopClarificationQuestion);
              setInlineFollowupMessageIndex(finalizedAssistantMessageIndex);
              console.log(
                '💭 [LOOP CLARIFICATION] Question ready:',
                loopClarificationQuestion.question,
                'at index',
                finalizedAssistantMessageIndex,
              );
              break;
            }

            console.log('💭 [FOLLOWUP] Loop-owned clarifications control inline follow-ups.');
            break;

          case 'augmentation_started':
            // Update last message to show augmentation is in progress
            setConversation((prev) => {
              const updated = [...prev];
              if (updated.length > 0) {
                updated[updated.length - 1] = {
                  ...updated[updated.length - 1],
                  isAugmenting: true,
                };
              }
              return updated;
            });
            break;

          case 'augmentation_complete':
            // Update last message with augmented content
            setConversation((prev) => {
              const updated = [...prev];
              if (updated.length > 0) {
                updated[updated.length - 1] = {
                  ...updated[updated.length - 1],
                  isAugmenting: false,
                  augmentedContent: event.content,
                  augmentedCitations: event.citations || [],
                };
              }
              return updated;
            });
            console.log('✨ [AUGMENTATION] Fresh content added');
            break;

          case 'augmentation_skipped':
          case 'augmentation_failed':
          case 'augmentation_timeout':
            // Hide augmentation indicator, no content to show
            setConversation((prev) => {
              const updated = [...prev];
              if (updated.length > 0) {
                updated[updated.length - 1] = {
                  ...updated[updated.length - 1],
                  isAugmenting: false,
                };
              }
              return updated;
            });
            console.log(`⏭️ [AUGMENTATION] ${event.type}: ${event.reason || event.error || ''}`);
            break;

          case 'error':
            throw new Error(event.message || 'Unknown streaming error');
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        watchdog.kick();
        const parsed = extractSseEvents(
          done ? '' : decoder.decode(value, { stream: true }),
          buffer,
          { flush: done },
        );
        buffer = parsed.carry;

        for (const event of parsed.events) {
          processEvent(event);
        }

        if (done) {
          break;
        }
      }

      if (!sawCompleteEvent) {
        console.error('Chat stream ended without a complete event', {
          sawDoneEvent,
          remainingBuffer: buffer,
          streamedAssistantKey,
          clientId,
          sessionId: currentSessionId,
        });
        setCurrentStage(null);
        setStatusMessage('');
        setCurrentTool(null);
        setCurrentToolQuery('');
        setCurrentToolTargets('');

        if (streamedAssistantKey) {
          const abortedStreamKey = streamedAssistantKey;
          setConversation((prev) =>
            prev.map((message) => {
              if (message?._streamKey !== abortedStreamKey) {
                return message;
              }
              const prefix =
                typeof message.content === 'string' && message.content.trim()
                  ? `${message.content}\n\n`
                  : '';
              return {
                ...message,
                content: `${prefix}${t('I hit a streaming error before the answer fully completed. Please try again.')}`,
                isStreaming: false,
                isSystemError: true,
                retryQuestion: userMessage,
                retryDisplayMessage: displayMessage,
              };
            }),
          );
        } else {
          setConversation((prev) => [
            ...prev,
            {
              role: 'assistant',
              content: t(
                'I hit a streaming error and could not finish that answer. Please try again.',
              ),
              citations: [],
              confidence: 'low',
              reasoning: 'Client-side streaming error',
              request_id: null,
              incident_id: null,
              retryQuestion: userMessage,
              retryDisplayMessage: displayMessage,
              isSystemError: true,
              isStreaming: false,
            },
          ]);
        }
      }
    } catch (error) {
      console.error('Chat error:', error);
      if (sawCompleteEvent) {
        return;
      }
      const failureCopy = timedOut
        ? t(
            'This is taking longer than usual and I could not finish the answer. Please send your message again.',
          )
        : t('I hit a streaming error before the answer fully completed. Please try again.');
      setCurrentStage(null);
      setStatusMessage('');
      setCurrentTool(null);
      setCurrentToolQuery('');
      setCurrentToolTargets('');
      setConversation((prev) => {
        const updated = [...prev];
        const streamingIndex = updated.findIndex(
          (message) => message?.role === 'assistant' && message?.isStreaming,
        );
        if (streamingIndex >= 0) {
          const existing = updated[streamingIndex];
          const prefix =
            typeof existing.content === 'string' && existing.content.trim()
              ? `${existing.content}\n\n`
              : '';
          updated[streamingIndex] = {
            ...existing,
            content: `${prefix}${failureCopy}`,
            isStreaming: false,
            isSystemError: true,
            retryQuestion: userMessage,
            retryDisplayMessage: displayMessage,
          };
          return updated;
        }

        updated.push({
          role: 'assistant',
          content: timedOut
            ? t(
                'This is taking longer than usual and I could not finish the answer. Please send your message again.',
              )
            : t('I hit a streaming error and could not finish that answer. Please try again.'),
          citations: [],
          confidence: 'low',
          reasoning: 'Client-side streaming error',
          request_id: null,
          incident_id: null,
          retryQuestion: userMessage,
          retryDisplayMessage: displayMessage,
          isSystemError: true,
          isStreaming: false,
        });
        return updated;
      });
    } finally {
      watchdog.stop();
      setStatusMessage('');
      setCurrentStage(null);
      setCurrentTool(null);
      setCurrentToolQuery('');
      setCurrentToolTargets('');
      setIsLoading(false);
      setQuestionSource('typed');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      autocomplete.closeSuggestions(); // Close dropdown before submit
      handleSubmit();
    }
  };

  // Handle autocomplete keyboard navigation
  const handleKeyDown = (e) => {
    const result = autocomplete.handleKeyDown(e);
    if (result?.selected) {
      // User pressed Enter on a selected suggestion
      handleSuggestionSelect(result.selected);
    }
  };

  // Handle suggestion selection
  const handleSuggestionSelect = (suggestion) => {
    autocomplete.selectSuggestion(suggestion);
    autocomplete.closeSuggestions(); // Explicitly close to prevent ghost dropdown
    setQuestionSource('autocomplete');
    // Submit with answer_id for direct fetch (bypasses semantic search)
    handleSubmit(null, suggestion.canonical_query, 'autocomplete', suggestion.answer_id);
  };

  // Type a canned query into the input box character-by-character, then submit.
  // Used by the `type_next_prompt` next-action chip to visually communicate
  // "the AI typed this for me." Cancellable if the user starts typing themselves.
  const animateAndSubmitNextAction = async (queryText) => {
    const text = typeof queryText === 'string' ? queryText : '';
    if (!text) return;
    cancelNextActionAnimationRef.current = false;
    // Per-character delay tuned so typical queries land in 300–500ms total.
    const perChar = Math.max(12, Math.min(35, Math.round(420 / Math.max(text.length, 1))));
    for (let i = 0; i <= text.length; i++) {
      if (cancelNextActionAnimationRef.current) return;
      setQuery(text.slice(0, i));
      await new Promise((r) => setTimeout(r, perChar));
    }
    await new Promise((r) => setTimeout(r, 120));
    if (cancelNextActionAnimationRef.current) return;
    setQuery('');
    handleSubmit(null, text, 'next_action');
  };

  // Dispatches a next-action chip click. Phase 1 only knows `type_next_prompt`;
  // unknown types are silently ignored (forward-compatibility with future types).
  const handleNextActionClick = (action) => {
    if (!action || typeof action !== 'object') return;
    if (action.type === 'type_next_prompt' && typeof action.query === 'string') {
      animateAndSubmitNextAction(action.query);
    }
  };

  // Wire up the ref so the auth hook can re-submit after verification
  submitAfterAuthRef.current = async (opts) => {
    await handleSubmit(null, opts.question, opts.source, null, opts.displayMessage, {
      skipUserEcho: true,
      forcedAuthSessionId: opts.forcedAuthSessionId,
    });
  };

  const resetConversation = () => {
    setConfirmingNewConversation(false);
    setEndConversationError(null);
    clearChatSession(clientId);
    setConversation([]);
    setSessionId(null);
    setReferralCode(launchReferral);
    setHandledByHuman(false);
    setAwaitingPerson(false);
    setConversationReadToken(null);
    setWhatsappAvailable(false);
    setContinuedOnWhatsapp(false);
    setWhatsappError(null);
    setUserInfo(null);
    setStatusMessage('');
    setCurrentStage(null);
    setCurrentTool(null);
    setCurrentToolQuery('');
    setCurrentToolTargets('');
    setInlineFollowupQuestion(null);
    setInlineFollowupMessageIndex(null);
    auth.setFooterAuthOpen(false);
    auth.setIsSeededLaunchPending(false);
    // Reset URL to client root (remove answer path)
    replaceUrl(
      `/${clientId}${launchReferral === null ? '' : `?ref=${encodeURIComponent(launchReferral)}`}`,
    );
  };

  // "Nova conversa" while a person is involved asks first, then ends the case on
  // the server, so the team is not left writing to a visitor who has gone
  // (docs/plans/2026-10-12-web-chat-refresh, D4).
  const [confirmingNewConversation, setConfirmingNewConversation] = useState(false);
  const [endingConversation, setEndingConversation] = useState(false);
  const [endConversationError, setEndConversationError] = useState(null);

  const clearConversation = () => {
    if (waitingForPerson) {
      setEndConversationError(null);
      setConfirmingNewConversation(true);
      return;
    }
    resetConversation();
  };

  const confirmNewConversation = async () => {
    setEndingConversation(true);
    setEndConversationError(null);
    const result = await endWebConversation(
      clientId,
      sessionId,
      getAnonymousUserId(),
      conversationReadToken,
    );
    setEndingConversation(false);
    if (result.error) {
      setEndConversationError(t('Could not end the conversation now. Please try again.'));
      return;
    }
    resetConversation();
  };

  const newConversationConfirm = confirmingNewConversation ? (
    <div
      role="alertdialog"
      aria-labelledby="new-conversation-confirm"
      className="mt-3 rounded-xl border border-slate-200 bg-white p-3 text-sm text-slate-700"
    >
      <p id="new-conversation-confirm">
        {t(
          'The team has been told about this conversation. If you start a new one, this conversation will end.',
        )}
      </p>
      {endConversationError && (
        <p role="alert" className="mt-1.5 text-[13px] text-red-700">
          {endConversationError}
        </p>
      )}
      <div className="mt-2.5 flex flex-wrap justify-end gap-2">
        <button
          type="button"
          onClick={() => setConfirmingNewConversation(false)}
          disabled={endingConversation}
          className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
        >
          {t('Continue here')}
        </button>
        <button
          type="button"
          onClick={confirmNewConversation}
          disabled={endingConversation}
          className="rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-700 disabled:opacity-60"
        >
          {t('End and start new')}
        </button>
      </div>
    </div>
  ) : null;

  // Handle inline follow-up answer
  const handleInlineFollowupAnswer = async (answer) => {
    if (!inlineFollowupQuestion || isInlineFollowupSubmitting) return;

    setIsInlineFollowupSubmitting(true);
    const analytics = buildAnalyticsPayload(auth.effectiveAuthSessionId);
    const userId = auth.effectiveAuthSessionId || analytics.anonymous_id;
    const isLoopClarification = inlineFollowupQuestion?.display?.source === 'loop_rag';
    const followupRequestId =
      (inlineFollowupMessageIndex != null
        ? conversation[inlineFollowupMessageIndex]?.request_id
        : null) || null;
    const persistencePromise = submitInlineFollowupAction(
      clientId,
      userId,
      'answer',
      inlineFollowupQuestion.question_id,
      answer,
      inlineFollowupQuestion.fact_template,
      inlineFollowupQuestion.question, // Include the question for context
      followupRequestId,
    ).catch((error) => {
      console.error('Failed to persist inline follow-up answer:', error);
      return null;
    });

    try {
      if (isLoopClarification) {
        const followUp = buildLoopClarificationFollowUp(answer, inlineFollowupQuestion);
        clearActiveClarification();
        await handleSubmit(
          null,
          followUp.requestQuestion,
          'loop_clarification',
          null,
          followUp.displayMessage,
        );
      } else {
        clearActiveClarification();
      }

      const result = await persistencePromise;
      if (result) {
        console.log('💭 [FOLLOWUP] Answer submitted:', answer, '| Response:', result);
      }
    } catch (error) {
      console.error('Failed to continue after clarification:', error);
    } finally {
      setIsInlineFollowupSubmitting(false);
    }
  };

  // Handle inline follow-up skip
  const handleInlineFollowupSkip = async () => {
    if (!inlineFollowupQuestion || isInlineFollowupSubmitting) return;

    setIsInlineFollowupSubmitting(true);
    const analytics = buildAnalyticsPayload(auth.effectiveAuthSessionId);
    const userId = auth.effectiveAuthSessionId || analytics.anonymous_id;
    const followupRequestId =
      (inlineFollowupMessageIndex != null
        ? conversation[inlineFollowupMessageIndex]?.request_id
        : null) || null;

    try {
      await submitInlineFollowupAction(
        clientId,
        userId,
        'skip',
        inlineFollowupQuestion.question_id,
        null,
        null,
        null,
        followupRequestId,
      );
      console.log('💭 [FOLLOWUP] Question skipped');
      clearActiveClarification();
    } catch (error) {
      console.error('Failed to skip inline follow-up question:', error);
    } finally {
      setIsInlineFollowupSubmitting(false);
    }
  };

  const handleRetrySystemError = async (message) => {
    if (!message?.retryQuestion || isLoading) return;
    await handleSubmit(
      null,
      message.retryQuestion,
      'system_error_retry',
      null,
      message.retryDisplayMessage || message.retryQuestion,
    );
  };

  const chat = {
    messages: conversation,
    messagesEndRef,
    focusInput: useCallback(() => inputRef.current?.focus(), []),
    sessionId, // Expose sessionId for debug panel
    userInfo, // Expose userInfo for session panel
    clientId, // Expose clientId for debug panel
    isAuthenticated: Boolean(auth.effectiveAuthSessionId),
    authIdentityLabel: auth.effectiveAuthIdentityPrimary || null,
    // Inline follow-up state
    inlineFollowupQuestion,
    inlineFollowupMessageIndex,
    isInlineFollowupSubmitting,
    onInlineFollowupAnswer: handleInlineFollowupAnswer,
    onInlineFollowupSkip: handleInlineFollowupSkip,
    onRetrySystemError: handleRetrySystemError,
    authBar: (
      <div className="flex items-center justify-between gap-3 rounded-full border border-slate-200 bg-slate-50/80 px-4 py-2 text-xs text-slate-600">
        <div className="flex min-w-0 items-center gap-2">
          <span
            className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
              auth.effectiveAuthSessionId
                ? auth.authSuccessPulse
                  ? 'bg-emerald-200 text-emerald-800 animate-pulse'
                  : 'bg-emerald-100 text-emerald-700'
                : 'bg-indigo-100 text-indigo-700'
            }`}
          >
            {auth.effectiveAuthSessionId ? '✓' : '🔒'}
          </span>
          {auth.effectiveAuthSessionId ? (
            <span className="truncate font-medium text-slate-800">
              {auth.effectiveAuthIdentityPrimary}
            </span>
          ) : (
            <span className="font-medium text-slate-800">Personal help</span>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {auth.effectiveAuthSessionId ? (
            auth.cookieAuthSessionId ? (
              <span className="rounded-full border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-500">
                SSO
              </span>
            ) : (
              <button
                type="button"
                onClick={auth.handleAuthLogout}
                className="cursor-pointer rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-medium text-slate-600 transition-colors hover:bg-slate-100"
              >
                Sign out
              </button>
            )
          ) : (
            <button
              type="button"
              onClick={auth.openAuthForm}
              className="cursor-pointer rounded-full border border-indigo-200 bg-white px-3 py-1 text-[11px] font-medium text-indigo-700 transition-colors hover:bg-indigo-50"
            >
              Sign in
            </button>
          )}
        </div>
      </div>
    ),
    windowAuthBar: auth.effectiveAuthSessionId ? (
      <div className="flex items-center justify-between gap-3 text-xs text-slate-600">
        <div className="flex min-w-0 items-center gap-2">
          <span
            className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
              auth.authSuccessPulse
                ? 'bg-emerald-200 text-emerald-800 animate-pulse'
                : 'bg-emerald-100 text-emerald-700'
            }`}
          >
            ✓
          </span>
          <span className="truncate font-medium text-slate-800">
            {auth.effectiveAuthIdentityPrimary}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          {auth.cookieAuthSessionId ? (
            <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-400">
              SSO
            </span>
          ) : (
            <button
              type="button"
              onClick={auth.handleAuthLogout}
              className="cursor-pointer text-[12px] font-medium text-slate-500 transition-colors hover:text-slate-700"
            >
              Sign out
            </button>
          )}
        </div>
      </div>
    ) : (
      <div className="flex items-center justify-center">
        <button
          type="button"
          onClick={auth.openAuthForm}
          className="inline-flex cursor-pointer items-center gap-2 text-[12px] font-medium text-indigo-700 transition-colors hover:text-indigo-800"
        >
          <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-indigo-100 text-[11px] text-indigo-700">
            🔒
          </span>
          <span>Sign in</span>
        </button>
      </div>
    ),
    windowConversationBar: auth.effectiveAuthSessionId ? (
      <div className="flex items-center justify-between gap-3 text-xs text-slate-600">
        <div className="flex min-w-0 items-center gap-2">
          <span
            className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
              auth.authSuccessPulse
                ? 'bg-emerald-200 text-emerald-800 animate-pulse'
                : 'bg-emerald-100 text-emerald-700'
            }`}
          >
            ✓
          </span>
          <span className="truncate font-medium text-slate-800">
            {auth.effectiveAuthIdentityPrimary}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          {auth.cookieAuthSessionId ? (
            <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-400">
              SSO
            </span>
          ) : (
            <button
              type="button"
              onClick={auth.handleAuthLogout}
              className="cursor-pointer text-[12px] font-medium text-slate-500 transition-colors hover:text-slate-700"
            >
              Sign out
            </button>
          )}
        </div>
      </div>
    ) : (
      <div className="flex items-center justify-start">
        <button
          type="button"
          onClick={auth.openAuthForm}
          className="inline-flex cursor-pointer items-center gap-2 text-[12px] font-medium text-indigo-700 transition-colors hover:text-indigo-800"
        >
          <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-indigo-100 text-[11px] text-indigo-700">
            🔒
          </span>
          <span>Sign in</span>
        </button>
      </div>
    ),
    conversationAuthBar: auth.effectiveAuthSessionId ? (
      <div className="flex items-center gap-2 text-xs text-slate-500">
        {auth.cookieAuthSessionId ? (
          <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-500">
            <span className="font-medium text-slate-700">{auth.effectiveAuthIdentityPrimary}</span>
          </span>
        ) : (
          <>
            <span
              className={`inline-flex items-center rounded-full border px-3 py-1 text-xs ${
                auth.authSuccessPulse
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-700 animate-pulse'
                  : 'border-slate-200 bg-white text-slate-500'
              }`}
            >
              <span className="font-medium">{auth.effectiveAuthIdentityPrimary}</span>
            </span>
            <button
              type="button"
              onClick={auth.handleAuthLogout}
              className="cursor-pointer rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-100"
            >
              Sign out
            </button>
          </>
        )}
      </div>
    ) : (
      <button
        type="button"
        onClick={auth.openAuthForm}
        className="cursor-pointer rounded-full border border-indigo-200 bg-white px-3 py-1 text-xs font-medium text-indigo-700 transition-colors hover:bg-indigo-50"
      >
        Sign in
      </button>
    ),
    authPrompt: auth.authPrompt,
    authMessageIndex: auth.authMessageIndex,
    authFormOpen: auth.authFormOpen,
    footerAuthOpen: auth.footerAuthOpen,
    authStep: auth.authStep,
    authEmail: auth.authEmail,
    authCode: auth.authCode,
    authManualCodeOpen: auth.authManualCodeOpen,
    authScenarioOptions: auth.authScenarioOptions,
    demoDrawerScenarioOptions:
      auth.demoScenarioOptions.length > 0 ? auth.demoScenarioOptions : auth.authScenarioOptions,
    preferredScenarioCode: auth.preferredScenarioCode,
    isSeededLaunchPending: auth.isSeededLaunchPending,
    authError: auth.authError,
    authSessionTtlDays: auth.authSessionTtlDays,
    isAuthSubmitting: auth.isAuthSubmitting,
    onAuthEmailChange: auth.setAuthEmail,
    onAuthCodeChange: auth.setAuthCode,
    onAuthStart: auth.handleAuthStart,
    onAuthVerify: auth.handleAuthVerify,
    onAuthSelectScenario: auth.handleAuthSelectScenario,
    onStartDrawerScenario: auth.handleStartDrawerScenario,
    onAuthToggleManualCode: () => auth.setAuthManualCodeOpen((prev) => !prev),
    onAuthOpenInline: auth.openInlineAuthForm,
    onAuthResetToEmail: () => {
      auth.resetAuthToEmail();
    },
    authInline:
      conversation.length === 0 && auth.footerAuthOpen ? (
        <div className="w-full max-w-lg">
          <InlineAuthCard
            prompt={auth.buildGenericAuthPrompt()}
            isOpen={true}
            step={auth.authStep}
            email={auth.authEmail}
            code={auth.authCode}
            manualCodeOpen={auth.authManualCodeOpen}
            preferredScenarioCode={auth.preferredScenarioCode}
            isDemoMode={auth.isDemoMode}
            scenarioOptions={
              auth.demoScenarioOptions.length > 0
                ? auth.demoScenarioOptions
                : auth.authScenarioOptions
            }
            error={auth.authError}
            ttlDays={auth.authSessionTtlDays}
            isSubmitting={auth.isAuthSubmitting}
            onEmailChange={auth.setAuthEmail}
            onCodeChange={auth.setAuthCode}
            onStart={auth.handleAuthStart}
            onVerify={auth.handleAuthVerify}
            onSelectScenario={auth.handleAuthSelectScenario}
            onToggleManualCode={() => auth.setAuthManualCodeOpen((prev) => !prev)}
            onResetToEmail={() => {
              auth.resetAuthToEmail();
            }}
            variant="landing"
          />
        </div>
      ) : null,
    conversationAuthInline:
      conversation.length > 0 && !auth.effectiveAuthSessionId && auth.footerAuthOpen ? (
        <div className="w-full max-w-lg">
          <InlineAuthCard
            prompt={auth.buildGenericAuthPrompt()}
            isOpen={true}
            step={auth.authStep}
            email={auth.authEmail}
            code={auth.authCode}
            manualCodeOpen={auth.authManualCodeOpen}
            preferredScenarioCode={auth.preferredScenarioCode}
            isDemoMode={auth.isDemoMode}
            scenarioOptions={
              auth.demoScenarioOptions.length > 0
                ? auth.demoScenarioOptions
                : auth.authScenarioOptions
            }
            error={auth.authError}
            ttlDays={auth.authSessionTtlDays}
            isSubmitting={auth.isAuthSubmitting}
            onEmailChange={auth.setAuthEmail}
            onCodeChange={auth.setAuthCode}
            onStart={auth.handleAuthStart}
            onVerify={auth.handleAuthVerify}
            onSelectScenario={auth.handleAuthSelectScenario}
            onToggleManualCode={() => auth.setAuthManualCodeOpen((prev) => !prev)}
            onResetToEmail={() => {
              auth.resetAuthToEmail();
            }}
          />
        </div>
      ) : null,
    input: (
      <div
        className={
          isWindowSurface
            ? 'flex flex-col gap-3'
            : 'flex flex-col gap-3 sm:flex-row sm:items-center'
        }
      >
        {whatsappAvailable && !continuedOnWhatsapp && (
          <div className="flex flex-col items-start gap-1">
            <button
              type="button"
              onClick={offerWhatsapp}
              disabled={offeringWhatsapp}
              data-testid="continue-on-whatsapp"
              className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-[13px] font-medium text-emerald-800 transition-colors hover:bg-emerald-100 disabled:cursor-default disabled:opacity-60"
            >
              {t('Continue on WhatsApp')}
            </button>
            {whatsappError && (
              <p role="alert" className="text-[12px] text-rose-700">
                {whatsappError}
              </p>
            )}
          </div>
        )}
        <div className="relative flex-1">
          <div
            className={`flex items-center gap-2 border ${isWindowSurface ? 'rounded-[18px] border-slate-200 bg-white px-4 py-3 shadow-sm shadow-slate-200/20' : 'rounded-full border-slate-200/80 bg-slate-50 px-5 py-4'}`}
          >
            <span className="text-slate-400 text-sm">✦</span>
            <input
              ref={inputRef}
              id="askInput"
              data-testid="ask-input"
              type="text"
              value={query}
              onChange={(e) => {
                // If a next-action typing animation is in flight, the user
                // typing wins — cancel the animation immediately.
                cancelNextActionAnimationRef.current = true;
                setQuery(e.target.value);
                setQuestionSource('typed');
                autocomplete.handleQueryChange(e.target.value);
              }}
              onFocus={autocomplete.handleFocus} // Lazy-load local embeddings on focus
              onKeyDown={handleKeyDown}
              onKeyPress={handleKeyPress}
              placeholder={
                (continuedOnWhatsapp && t('This conversation continues on WhatsApp.')) ||
                clientConfig?.askPlaceholder ||
                (isWindowSurface
                  ? 'Ask about membership, credits, exams, or renewal...'
                  : 'Ask anything about your membership, learning, events, or payments…')
              }
              className={`flex-1 bg-transparent outline-none placeholder:text-slate-400 ${isWindowSurface ? 'text-[15px]' : 'text-base'}`}
              disabled={isLoading || continuedOnWhatsapp}
            />
            {isWindowSurface && (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isLoading || !query.trim()}
                aria-label={t('Send message')}
                className="inline-flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-full bg-slate-100 text-slate-500 transition-colors hover:bg-slate-200 hover:text-slate-700 disabled:cursor-default disabled:opacity-50"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.8"
                    d="M7 11.5 17.5 6.5 12.5 17l-1.8-4.2L7 11.5Z"
                  />
                </svg>
              </button>
            )}
          </div>

          {/* Autocomplete suggestions dropdown
              Suppressed for the Sarah Chen demo persona (member_id 900333) to
              prevent the curated follow-up phrases from leaking via type-ahead. */}
          {autocomplete.showSuggestions && String(auth.authProfile?.profile?.id) !== '900333' && (
            <AutocompleteSuggestions
              suggestions={autocomplete.suggestions}
              selectedIndex={autocomplete.selectedIndex}
              isLoading={autocomplete.isLoading}
              onSelect={handleSuggestionSelect}
              onClose={autocomplete.closeSuggestions}
              onMouseActivity={autocomplete.resetAutoHideTimer}
              timing={autocomplete.timing}
            />
          )}
        </div>
        {!isWindowSurface && (
          <button
            onClick={handleSubmit}
            disabled={isLoading || !query.trim()}
            className="inline-flex cursor-pointer items-center justify-center gap-1 rounded-full bg-indigo-600 px-5 py-3 text-[12px] font-medium whitespace-nowrap text-white transition-colors hover:bg-indigo-700 disabled:cursor-default disabled:opacity-50"
          >
            <span>{t('Ask')}</span>
            <span>↵</span>
          </button>
        )}
      </div>
    ),
    isLoading,
    statusMessage,
    currentStage,
    currentTool,
    currentToolQuery,
    currentToolTargets,
    clearConversation,
    newConversationConfirm,
    // Demo persona signal — true when authenticated as Sarah Chen (900333),
    // used by ChatMessages to suppress the ⚡ Instant Answer chip.
    hideInstantAnswerChip: String(auth.authProfile?.profile?.id) === '900333',
    onNextActionClick: handleNextActionClick,
    onHandoverFormSubmit: sendHandoverForm,
  };

  // Offer to sign in only where this tenant can verify somebody. The backend
  // gate reads the same key (`concierge-api.member_auth_enabled`), so the
  // browser is told the capability rather than assuming it: all 19 tenants
  // showed a "Personal help / Sign in" chip, not one of them had an sso_url or
  // any way to verify a person, and npas asked a Brazilian parent to sign in
  // to an account that does not exist anywhere.
  //
  // Suppressed here, in one place, rather than at each of the six surfaces
  // below — three bars, a window variant and two inline cards. Gating them
  // one by one is how the seventh gets missed, and a browser found two of
  // these after the first attempt gated only the one in the screenshot.
  if (!clientConfig?.member_auth) {
    for (const surface of [
      'authBar',
      'windowAuthBar',
      'windowConversationBar',
      'conversationAuthBar',
      'authInline',
      'conversationAuthInline',
    ]) {
      chat[surface] = null;
    }
  }

  return chat;
}

/**
 * DebugPanel - Shows session context (dev mode only)
 * Uses DevModeContext for shared visibility state with DevModePanel
 */
function DebugPanel({ clientId, sessionId, messageCount }) {
  const { isDevMode, panelVisible, togglePanel } = useDevMode();

  // Don't render in production
  if (!isDevMode) return null;

  // Collapsed toggle button (same as DevModePanel)
  if (!panelVisible) {
    return (
      <button
        onClick={togglePanel}
        className="fixed top-2 right-2 z-50 p-2 bg-slate-800 text-white text-xs rounded-lg shadow-lg hover:bg-slate-700 transition-colors"
        title="Show Dev Panel"
        data-testid="dev-panel-toggle"
      >
        🔧 Dev
      </button>
    );
  }

  // Parse cookies
  const allCookies = document.cookie.split(';').reduce((acc, cookie) => {
    const [name, value] = cookie.trim().split('=');
    acc[name] = value;
    return acc;
  }, {});

  // Parse session cookie if present
  let sessionCookie = null;
  if (allCookies.session) {
    try {
      const decoded = decodeURIComponent(allCookies.session);
      sessionCookie = JSON.parse(decoded);
    } catch (e) {
      sessionCookie = { error: 'Failed to parse', raw: allCookies.session };
    }
  }

  // Get analytics data
  const analytics = buildAnalyticsPayload(sessionCookie?.session_id || null);
  const { anonymous_id, client_info } = analytics;

  return (
    <div
      className="mb-6 p-4 bg-slate-100 rounded-lg border border-slate-300 text-xs font-mono relative"
      data-testid="debug-panel"
    >
      {/* Close button */}
      <button
        onClick={togglePanel}
        className="absolute top-2 right-2 p-1 text-slate-500 hover:text-slate-700"
        title="Hide Dev Panel"
        data-testid="debug-panel-close"
      >
        ✕
      </button>

      <div className="font-bold mb-3 text-slate-700 flex items-center gap-2">
        <span>🔍</span>
        <span>Session Context (Dev Mode)</span>
      </div>
      <div className="space-y-1.5 text-slate-600">
        <div className="flex gap-2">
          <span className="font-semibold min-w-24">Client ID:</span>
          <span className="text-indigo-600 font-medium">{clientId}</span>
        </div>
        <div className="flex gap-2">
          <span className="font-semibold min-w-24">Session ID:</span>
          <span className="text-green-600 font-medium">{sessionId || 'Not created yet'}</span>
        </div>
        <div className="flex gap-2">
          <span className="font-semibold min-w-24">Messages:</span>
          <span className="text-amber-600 font-medium">{messageCount}</span>
        </div>

        {/* Cookie info */}
        <div className="pt-2 mt-2 border-t border-slate-300">
          <div className="font-semibold mb-1.5 flex items-center gap-1">
            <span>🍪</span>
            <span>Cookies (.app.thoughtfabric.com)</span>
          </div>
          <div className="flex gap-2">
            <span className="font-semibold min-w-24">Total:</span>
            <span className="text-purple-600 font-medium">{Object.keys(allCookies).length}</span>
          </div>
          {sessionCookie ? (
            <div className="mt-1.5">
              <div className="font-semibold mb-1">Session Cookie:</div>
              <pre className="bg-white p-2 rounded border border-slate-200 text-[10px] overflow-x-auto max-w-full">
                {JSON.stringify(sessionCookie, null, 2)}
              </pre>
            </div>
          ) : (
            <div className="flex gap-2 mt-1">
              <span className="font-semibold min-w-24">Session Cookie:</span>
              <span className="text-red-600 font-medium">Not found</span>
            </div>
          )}
        </div>

        {/* Analytics info */}
        <div className="pt-2 mt-2 border-t border-slate-300">
          <div className="font-semibold mb-1.5 flex items-center gap-1">
            <span>📊</span>
            <span>Analytics</span>
          </div>
          <div className="flex gap-2">
            <span className="font-semibold min-w-24">Anonymous ID:</span>
            <span className="text-blue-600 font-medium">{anonymous_id || '(authenticated)'}</span>
          </div>
          <div className="flex gap-2">
            <span className="font-semibold min-w-24">Browser:</span>
            <span className="text-slate-600">{client_info.browser}</span>
          </div>
          <div className="flex gap-2">
            <span className="font-semibold min-w-24">Platform:</span>
            <span className="text-slate-600">{client_info.platform}</span>
          </div>
          <div className="flex gap-2">
            <span className="font-semibold min-w-24">Language:</span>
            <span className="text-slate-600">{client_info.language}</span>
          </div>
          <div className="flex gap-2">
            <span className="font-semibold min-w-24">Timezone:</span>
            <span className="text-slate-600">{client_info.timezone}</span>
          </div>
          <div className="flex gap-2">
            <span className="font-semibold min-w-24">Viewport:</span>
            <span className="text-slate-600">
              {client_info.viewport_width}x{client_info.viewport_height}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function InlineAuthCard({
  prompt,
  isOpen,
  step,
  email,
  code,
  manualCodeOpen = false,
  preferredScenarioCode = null,
  isDemoMode = false,
  scenarioOptions = [],
  error,
  ttlDays,
  isSubmitting,
  onEmailChange,
  onCodeChange,
  onOpen,
  onStart,
  onVerify,
  onSelectScenario,
  onToggleManualCode,
  onResetToEmail,
  variant = 'inline',
}) {
  const codeInputRef = useRef(null);

  const isLanding = variant === 'landing';
  const isInline = variant === 'inline';
  const hasPrompt = Boolean(prompt);
  const scenarioPlaceholder =
    scenarioOptions.length > 0
      ? `Use ${scenarioOptions
          .slice(0, 2)
          .map((scenario) => {
            const rawName = String(
              scenario.display_name || scenario.hint || scenario.label || '',
            ).trim();
            const shortName = rawName.split(/\s+/)[0] || rawName || 'member';
            return `${scenario.code} for ${shortName}`;
          })
          .join(' or ')}`
      : `Use ${MOCK_AUTH_SUCCESS_CODE} for Taylor or 22222 for Casey`;
  const codePlaceholder = isDemoMode
    ? scenarioPlaceholder
    : prompt?.code_label || 'Verification code';

  useEffect(() => {
    if (!hasPrompt || !isOpen || isSubmitting) return undefined;

    const target = codeInputRef.current;
    if (!target) return undefined;

    const focusId = window.requestAnimationFrame(() => {
      target.focus();
      target.select();
    });

    return () => window.cancelAnimationFrame(focusId);
  }, [hasPrompt, isOpen, isSubmitting, step]);

  if (!prompt) return null;

  if (isLanding && !isOpen) return null;

  if (isInline && !isOpen) {
    return (
      <div className="mt-3 mb-1 max-w-xl animate-fadeIn">
        <div className="flex flex-col gap-2 rounded-2xl border border-slate-200 bg-slate-50/80 px-3 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-indigo-700">
              🔒
            </span>
            <span>Sign in to access your member information.</span>
          </div>
          <button
            type="button"
            onClick={onOpen}
            className="inline-flex shrink-0 items-center justify-center rounded-full border border-indigo-200 bg-white px-3 py-1.5 text-[11px] font-medium text-indigo-700 transition-colors hover:bg-indigo-50"
          >
            Sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`mt-3 mb-1 animate-fadeIn ${isLanding ? 'mx-auto max-w-xl' : 'max-w-xl'}`}>
      <div className={isLanding ? 'space-y-2' : ''}>
        <form
          onSubmit={onVerify}
          className="flex items-stretch overflow-hidden rounded-full border border-slate-200 bg-white"
        >
          <input
            ref={codeInputRef}
            type="text"
            value={code}
            onChange={(e) => onCodeChange(normalizeMockAuthCodeInput(e.target.value))}
            placeholder={codePlaceholder}
            className="min-w-0 flex-1 bg-transparent px-4 py-2 text-sm outline-none"
            inputMode="numeric"
            pattern="[0-9]{5}"
            maxLength={5}
            autoComplete="one-time-code"
            disabled={isSubmitting}
          />
          <button
            type="submit"
            disabled={isSubmitting}
            className="shrink-0 border-l border-indigo-500 bg-indigo-600 px-4 py-2 text-xs font-medium text-white transition-colors hover:bg-indigo-700 disabled:opacity-50"
          >
            {isSubmitting ? 'Working...' : prompt.verify_label || 'Verify'}
          </button>
        </form>
        {error && (
          <p className={`mt-2 text-xs text-red-600 ${isLanding ? 'text-center' : ''}`}>{error}</p>
        )}
      </div>
    </div>
  );
}

/**
 * ChatMessages - Renders conversation history
 */
export function ChatMessages({
  messages,
  messagesEndRef,
  isLoading,
  statusMessage = '',
  currentStage = null,
  currentTool = null,
  currentToolQuery = '',
  currentToolTargets = '',
  sessionId,
  clientId,
  // Inline follow-up props
  inlineFollowupQuestion = null,
  inlineFollowupMessageIndex = null,
  isInlineFollowupSubmitting = false,
  onInlineFollowupAnswer = null,
  onInlineFollowupSkip = null,
  onRetrySystemError = null,
  authPrompt = null,
  authMessageIndex = null,
  authFormOpen = false,
  authStep = 'code',
  authEmail = '',
  authCode = '',
  authManualCodeOpen = false,
  authScenarioOptions = [],
  preferredScenarioCode = null,
  authError = '',
  authSessionTtlDays = 60,
  isAuthSubmitting = false,
  hideInstantAnswerChip = false,
  onNextActionClick = null,
  onAuthEmailChange = null,
  onAuthCodeChange = null,
  onAuthStart = null,
  onAuthVerify = null,
  onAuthSelectScenario = null,
  onAuthToggleManualCode = null,
  onAuthOpenInline = null,
  onAuthResetToEmail = null,
  onHandoverFormSubmit = null,
}) {
  const { clientConfig } = useClient();
  const t = useCopy();
  // The stage pill ("RETRIEVAL", "SEARCH") narrates a search, which a Portuguese
  // tenant's rules forbid saying; the status line under it carries the turn.
  const showStageLabel = !speaksPortuguese(clientConfig?.locale);
  const conciergeName = clientConfig?.conciergeName || 'Conci';
  // What a person from the team is called under their own words. Never the
  // assistant's name: a manager's reply signed "Estela" tells the visitor a
  // machine wrote it, which is the same lie in the other direction as
  // presenting generated text as delivered.
  const teamName = clientConfig?.orgShortName ? `Equipe ${clientConfig.orgShortName}` : 'Equipe';
  const hasStreamingAssistantMessage = messages.some(
    (message) => message?.role === 'assistant' && message?.isStreaming,
  );

  // Anchor the most-recent user question near the top of the scroll container
  // when a new question is submitted. The answer then streams downward into
  // view, instead of the page jumping to the bottom and forcing the user to
  // scroll back up. Token deltas and augmentation events do not retrigger this.
  const lastUserMessageRef = useRef(null);
  const lastUserMessageIndex = useMemo(
    () => messages.findLastIndex((m) => m?.role === 'user'),
    [messages],
  );
  const userMessageCount = useMemo(
    () => messages.filter((m) => m?.role === 'user').length,
    [messages],
  );
  // Where a person takes over and where the assistant answers again, so the
  // visitor sees the moment a human is on the other side rather than having
  // to spot a 10px label under an identical bubble.
  const dividers = useMemo(() => handoverDividers(messages), [messages]);
  // One form per conversation: only the newest offer is shown, because each
  // offer rotates the token the older one was given.
  const latestFormIndex = useMemo(
    () => messages.findLastIndex((m) => m?.role === 'assistant' && m?.handoverForm),
    [messages],
  );
  const formLeadIn = t('So the team can get back to you, leave a contact:');
  const personAccent = clientConfig?.primaryColor;
  const orgShortName = clientConfig?.orgShortName;
  useEffect(() => {
    if (userMessageCount === 0) return;
    const target = lastUserMessageRef.current;
    if (!target) return;
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    requestAnimationFrame(() => {
      target.scrollIntoView({
        block: 'start',
        behavior: prefersReducedMotion ? 'instant' : 'smooth',
      });
    });
  }, [userMessageCount]);

  if (messages.length === 0) {
    return null;
  }

  // Shared bubble shadow
  const bubbleShadow = { boxShadow: '0 2px 12px -2px rgba(0,0,0,0.07)' };

  // Small grey label rendered below each assistant bubble (settled, streaming,
  // or loading). iMessage "Delivered" style — calm identity without the
  // attention-grabbing red dot + cursor that used to sit above every bubble.
  // Right-aligned so it sits opposite the feedback buttons (left), like a
  // signature at the end of the message.
  const ConciergeFooter = ({ extra, fromHuman = false } = {}) => (
    <div className="mt-1 flex items-center justify-end gap-1.5 px-1">
      {!fromHuman && <p className="text-[10px] text-slate-400">{conciergeName}</p>}
      {extra}
    </div>
  );

  return (
    <div className="space-y-5">
      {messages.map((message, index) => {
        const liveWebMeta = getLiveWebMeta(message);

        /* ── USER BUBBLE ── */
        if (message.role === 'user') {
          const isLatestUserMessage = index === lastUserMessageIndex;
          return (
            <div
              key={index}
              ref={isLatestUserMessage ? lastUserMessageRef : null}
              className="flex flex-col items-end scroll-mt-4"
              aria-label={t('You said')}
            >
              <div className="max-w-[82%]">
                <div
                  className="bg-white border border-stone-200 rounded-2xl rounded-tr-sm px-4 py-3"
                  style={bubbleShadow}
                >
                  <p className="text-sm text-slate-900 leading-relaxed font-medium">
                    {message.content}
                  </p>
                </div>
              </div>
            </div>
          );
        }

        /* ── AI BUBBLE ── */
        const structuredDisplay = getStructuredDisplay(message);
        const hasEventCards = Boolean(structuredDisplay);
        // When event cards are present, filter out citations that duplicate card content.
        // Match by URL slug (after last colon or slash) since the same course can have
        // different listing IDs (e.g., 337255mb: vs 352188mb: for the same slug).
        const urlSlug = (url) => {
          if (!url) return '';
          const colonIdx = url.lastIndexOf(':');
          return colonIdx > 8 ? url.slice(colonIdx + 1) : url;
        };
        const eventCardSlugs = hasEventCards
          ? new Set(
              (structuredDisplay.items || []).map((item) => urlSlug(item.url)).filter(Boolean),
            )
          : null;
        const filteredCitations =
          eventCardSlugs && message.citations
            ? message.citations.filter((c) => !eventCardSlugs.has(urlSlug(c.url)))
            : message.citations;
        const hasCitations = filteredCitations && filteredCitations.length > 0;
        // No confidence badge: a family told an answer has "medium confidence"
        // has a reason to doubt it (NPAS Stage 1 run 2; Alex, 2026-10-09: removed
        // for every tenant). Staff still see confidence in the admin and traces.
        const hasBadges = isCuratedAnswer(message) && hideInstantAnswerChip !== true;
        const hasAugmented = Boolean(message.augmentedContent);
        // Phase 1 only renders type_next_prompt; future types will add branches in
        // handleNextActionClick. Silently filter unknowns so newer-type actions
        // deployed elsewhere don't break this build.
        const visibleNextActions = Array.isArray(message.nextActions)
          ? message.nextActions.filter((a) => a && a.type === 'type_next_prompt' && a.query)
          : [];
        const hasNextActions = visibleNextActions.length > 0;
        const hasBottomSection = hasBadges || hasCitations || hasNextActions;

        const divider = dividers[index];
        const showsForm = index === latestFormIndex && Boolean(onHandoverFormSubmit);
        // A request for a person has no reply of its own: its answer is the
        // card's lead-in, which the card already says.
        const formOnly = showsForm && (message.content || '').trim() === formLeadIn;

        return (
          <div key={index} className="flex flex-col items-start">
            {divider && (
              <div className="mb-4 flex w-full items-center gap-3" role="status">
                <div className="h-px flex-1 bg-stone-200" />
                <p className="text-[11px] font-medium text-slate-500">
                  {divider === 'joined'
                    ? `Uma pessoa da equipe${orgShortName ? ` ${orgShortName}` : ''} entrou na conversa`
                    : `${conciergeName} voltou a responder`}
                </p>
                <div className="h-px flex-1 bg-stone-200" />
              </div>
            )}
            <div className="max-w-[88%] w-full">
              {/* Named once per run of a person's messages, like a chat app does. */}
              {message.fromHuman && !messages[index - 1]?.fromHuman && (
                <div
                  className="mb-1 flex items-center gap-1.5 pl-1"
                  style={{ color: personAccent }}
                >
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="h-3.5 w-3.5"
                  >
                    <path d="M10 10a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm-7 8a7 7 0 1 1 14 0H3Z" />
                  </svg>
                  <p className="text-xs font-semibold">{teamName}</p>
                </div>
              )}
              {/* Main bubble */}
              <div
                hidden={formOnly}
                className={`border rounded-2xl rounded-tl-sm px-4 py-3 ${
                  message.fromHuman
                    ? 'border-l-[3px]'
                    : `bg-white ${message.isSystemError ? 'border-rose-200' : 'border-stone-200'}`
                }`}
                style={
                  message.fromHuman
                    ? {
                        ...bubbleShadow,
                        backgroundColor: `color-mix(in srgb, ${personAccent} 6%, white)`,
                        borderColor: `color-mix(in srgb, ${personAccent} 22%, white)`,
                        borderLeftColor: personAccent,
                      }
                    : bubbleShadow
                }
              >
                {/* Streaming status (shown while answer streams in) */}
                {message.isStreaming && (
                  <div className="mb-2">
                    <StreamStatusPanel
                      currentStage={currentStage}
                      statusMessage={statusMessage}
                      currentTool={currentTool}
                      currentToolQuery={currentToolQuery}
                      currentToolTargets={currentToolTargets}
                      showStageLabel={showStageLabel}
                    />
                  </div>
                )}

                {/* Answer text */}
                {message.content && (
                  <div
                    className="text-sm leading-relaxed text-slate-700 html-content [&>*:first-child]:mt-0"
                    dangerouslySetInnerHTML={{
                      __html: parseMarkdown(
                        message.content,
                        message.citations,
                        isCuratedAnswer(message),
                      ),
                    }}
                  />
                )}

                {/* Skill display */}
                {hasEventCards && <SkillDisplay display={structuredDisplay} />}

                {/* System error detail */}
                {message.isSystemError && (
                  <div className="mt-2 text-xs text-rose-700">
                    {/* In the visitor's language, and only with a code to quote: a
                        timed-out request has none, and "Request ID: pending" in
                        English followed NPAS's Portuguese timeout copy (run 3). */}
                    {message.request_id && (
                      <div className="mt-1">
                        {t('Reference')}: <span className="font-mono">{message.request_id}</span>
                        {message.incident_id && (
                          <>
                            {' '}
                            | <span className="font-mono">{message.incident_id}</span>
                          </>
                        )}
                      </div>
                    )}
                    <div className="mt-2">
                      <button
                        type="button"
                        onClick={() => onRetrySystemError?.(message)}
                        className="inline-flex items-center rounded-full border border-rose-200 bg-white px-3 py-1 text-[11px] font-medium text-rose-700 transition-colors hover:bg-rose-50"
                      >
                        {t('Retry')}
                      </button>
                    </div>
                  </div>
                )}

                {/* Augmented "See Also" content */}
                {hasAugmented && (
                  <div className="mt-3 pt-3 border-t border-stone-100">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                        {t('See Also')}
                      </span>
                    </div>
                    <div
                      className="text-sm text-slate-700 prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{
                        __html: parseMarkdown(
                          message.augmentedContent,
                          message.augmentedCitations,
                          false,
                        ),
                      }}
                    />
                    {message.augmentedCitations && message.augmentedCitations.length > 0 && (
                      <div className="mt-2 space-y-1.5">
                        {message.augmentedCitations.map((citation, i) => (
                          <SourceCard
                            key={`aug-${i}`}
                            url={citation.url}
                            title={citation.title}
                            summary={citation.summary}
                            tags={citation.tags}
                            index={i}
                            content_type={citation.content_type}
                            content_authority={citation.content_authority}
                            doc_date={citation.doc_date}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Augmenting loading indicator */}
                {message.isAugmenting && (
                  <div className="mt-3 pt-3 border-t border-stone-100 flex items-center gap-2 text-xs text-slate-400">
                    <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                    <span>Checking for fresher details (a few seconds)…</span>
                  </div>
                )}

                {/* Badges + sources — inside bubble, below border-t */}
                {hasBottomSection && (
                  <div className="mt-3 pt-3 border-t border-stone-100 space-y-2">
                    {hasBadges && (
                      <div className="flex flex-wrap gap-1.5">
                        {isCuratedAnswer(message) && hideInstantAnswerChip !== true && (
                          <span className="inline-flex items-center gap-1 rounded-full border border-yellow-200 bg-yellow-50 px-2.5 py-0.5 text-[11px] font-medium text-yellow-700">
                            {t('⚡ Instant Answer')}
                          </span>
                        )}
                      </div>
                    )}
                    {hasNextActions && (
                      <div className="flex flex-wrap gap-2 pt-1">
                        {visibleNextActions.map((action, actionIdx) => (
                          <button
                            key={`${index}-action-${actionIdx}`}
                            type="button"
                            onClick={() => onNextActionClick && onNextActionClick(action)}
                            className="inline-flex items-center gap-1.5 rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-[12px] font-medium text-sky-700 transition-colors hover:bg-sky-100 hover:border-sky-300 focus:outline-none focus:ring-2 focus:ring-sky-300"
                          >
                            <span aria-hidden="true">✦</span>
                            <span>{action.label}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {hasCitations && (
                      <CitationList
                        citations={filteredCitations}
                        clientId={clientId}
                        requestId={message.request_id}
                      />
                    )}
                  </div>
                )}
              </div>

              {showsForm && (
                <div className={formOnly ? '' : 'mt-2'}>
                  <HandoverFormCard
                    form={message.handoverForm}
                    sent={message.handoverFormSent || null}
                    whatsappAvailable={message.handoverFormWhatsapp}
                    accent={personAccent}
                    onSubmit={(fields, sent) =>
                      onHandoverFormSubmit(message.handoverForm, fields, sent)
                    }
                  />
                </div>
              )}

              {/* Auth prompt — outside bubble (interactive card) */}
              {authPrompt && authMessageIndex === index && (
                <div className="mt-2">
                  <InlineAuthCard
                    prompt={authPrompt}
                    isOpen={authFormOpen}
                    step={authStep}
                    email={authEmail}
                    code={authCode}
                    manualCodeOpen={authManualCodeOpen}
                    preferredScenarioCode={preferredScenarioCode}
                    isDemoMode={Boolean(
                      clientConfig?.liveDemoPage?.enabled ||
                      (clientConfig?.drawer?.enabled && clientConfig?.drawer?.mode === 'demo-info'),
                    )}
                    scenarioOptions={authScenarioOptions}
                    error={authError}
                    ttlDays={authSessionTtlDays}
                    isSubmitting={isAuthSubmitting}
                    onEmailChange={onAuthEmailChange}
                    onCodeChange={onAuthCodeChange}
                    onOpen={onAuthOpenInline}
                    onStart={onAuthStart}
                    onVerify={onAuthVerify}
                    onSelectScenario={onAuthSelectScenario}
                    onToggleManualCode={onAuthToggleManualCode}
                    onResetToEmail={onAuthResetToEmail}
                  />
                </div>
              )}

              {/* Inline follow-up — outside bubble */}
              {(() => {
                const inlineQuestion =
                  message.clarificationQuestion ||
                  (inlineFollowupQuestion && inlineFollowupMessageIndex === index
                    ? inlineFollowupQuestion
                    : null);
                if (!inlineQuestion) return null;
                return (
                  <div className="mt-2">
                    <InlineFollowupQuestion
                      question={inlineQuestion}
                      onAnswer={onInlineFollowupAnswer}
                      onSkip={onInlineFollowupSkip}
                      isSubmitting={isInlineFollowupSubmitting}
                    />
                  </div>
                );
              })()}

              {/* Meta row — feedback on left, concierge identity on right */}
              <div className="mt-2 flex items-center gap-2 pl-1">
                {message.request_id &&
                  !message.authGated &&
                  !message.isSystemError &&
                  !message.fromHuman &&
                  !formOnly && (
                    <FeedbackButtons
                      requestId={message.request_id}
                      clientId={clientId}
                      onFeedback={submitFeedback}
                    />
                  )}
                <div className="ml-auto">
                  <ConciergeFooter
                    fromHuman={message.fromHuman}
                    extra={
                      liveWebMeta && (
                        <span className="inline-flex h-4 items-center rounded-full border border-sky-200 bg-sky-50 px-2 text-[9px] font-semibold uppercase tracking-[0.12em] text-sky-700">
                          Live Web
                        </span>
                      )
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        );
      })}

      {/* Loading bubble */}
      {isLoading && !hasStreamingAssistantMessage && (
        <div className="flex flex-col items-start">
          <div className="max-w-[88%]">
            <div
              className="bg-white border border-stone-200 rounded-2xl rounded-tl-sm px-4 py-3"
              style={bubbleShadow}
            >
              <div className="flex items-center gap-2.5">
                <div className="flex gap-1">
                  <div
                    className="w-1.5 h-1.5 rounded-full bg-stone-300 animate-bounce-custom"
                    style={{ animationDelay: '0ms' }}
                  />
                  <div
                    className="w-1.5 h-1.5 rounded-full bg-stone-300 animate-bounce-custom"
                    style={{ animationDelay: '150ms' }}
                  />
                  <div
                    className="w-1.5 h-1.5 rounded-full bg-stone-300 animate-bounce-custom"
                    style={{ animationDelay: '300ms' }}
                  />
                </div>
                {(statusMessage || (currentStage && showStageLabel)) && (
                  <span className="text-xs text-slate-400">
                    {statusMessage || getStageLabel(currentStage)}
                  </span>
                )}
              </div>
              {currentTool === 'search_web' && (
                <div className="mt-2">
                  <StreamStatusPanel
                    currentStage={currentStage}
                    statusMessage={null}
                    currentTool={currentTool}
                    currentToolQuery={currentToolQuery}
                    currentToolTargets={currentToolTargets}
                    showStageLabel={showStageLabel}
                  />
                </div>
              )}
            </div>
            <ConciergeFooter />
          </div>
        </div>
      )}

      {/* Scroll anchor */}
      <div ref={messagesEndRef} />

      {/* Debug Panel (dev mode only) */}
      <DebugPanel clientId={clientId} sessionId={sessionId} messageCount={messages.length} />
    </div>
  );
}
